import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Products API
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Suppliers API
export const suppliersApi = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  updateProfile: (data) => api.put('/suppliers/profile', data),
};

// RFQs API
export const rfqsApi = {
  getAll: (params) => api.get('/rfqs', { params }),
  getById: (id) => api.get(`/rfqs/${id}`),
  create: (data) => api.post('/rfqs', data),
  respond: (id, data) => api.post(`/rfqs/${id}/respond`, data),
  submitQuote: (id, data) => api.post(`/rfqs/${id}/quote`, data),
  getQuotes: (id) => api.get(`/rfqs/${id}/quotes`),
  getMatchingSuppliers: (id) => api.get(`/rfqs/${id}/matching-suppliers`),
};

// Inquiries API
export const inquiriesApi = {
  create: (data) => api.post('/inquiries', data),
};

// Conversations API
export const conversationsApi = {
  getAll: () => api.get('/conversations'),
  getMessages: (id) => api.get(`/conversations/${id}/messages`),
  sendMessage: (id, data) => api.post(`/conversations/${id}/messages`, data),
};

// Search API
export const searchApi = {
  global: (params) => api.get('/search', { params }),
  suggestions: (query, type) => api.post('/search/suggestions', { query, type }),
};

// Chatbot API
export const chatbotApi = {
  sendMessage: (message, sessionId) => api.post('/chatbot/message', { message, session_id: sessionId }),
};

// Categories API
export const categoriesApi = {
  getAll: () => api.get('/categories'),
};

// Countries API
export const countriesApi = {
  getAll: () => api.get('/countries'),
};

// Membership API
export const membershipApi = {
  getPlans: () => api.get('/memberships/plans'),
  checkout: (planId, originUrl) => api.post('/memberships/checkout', { plan_id: planId, origin_url: originUrl }),
  checkStatus: (sessionId) => api.get(`/memberships/checkout/status/${sessionId}`),
  getStatus: () => api.get('/membership/status'),
};

// Billing API (Stripe subscriptions)
export const billingApi = {
  getPlans: () => api.get('/billing/plans'),
  createCheckout: (plan, interval) => api.post('/billing/create-checkout-session', { plan, interval }),
  checkoutStatus: (sessionId) => api.get(`/billing/checkout-status/${sessionId}`),
  getMyBilling: () => api.get('/billing/me'),
  createPortal: () => api.post('/billing/create-portal-session'),
  cancel: (cancelAtPeriodEnd = true) => api.post('/billing/cancel', { cancel_at_period_end: cancelAtPeriodEnd }),
  testCheckout: () => api.post('/billing/test-checkout'),
};

// Leads API
export const leadsApi = {
  getAll: (params) => api.get('/leads', { params }),
  updateStatus: (responseId, status) => api.put(`/leads/${responseId}/status`, { status }),
};

// Verification API
export const verificationApi = {
  getCountries: (q) => api.get('/verification/countries', { params: { q } }),
  getPhoneCodes: () => api.get('/verification/phone-codes'),
  step1: (data) => api.post('/verification/onboarding/step1', data),
  step2: (data) => api.put('/verification/onboarding/step2', data),
  step3: (data) => api.put('/verification/onboarding/step3', data),
  uploadDoc: (formData) => api.post('/verification/onboarding/step4/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDocuments: () => api.get('/verification/onboarding/documents'),
  step5: () => api.put('/verification/onboarding/step5'),
  submit: () => api.post('/verification/onboarding/submit'),
  getStatus: () => api.get('/verification/onboarding/status'),
  // Admin
  adminQueue: (params) => api.get('/verification/admin/queue', { params }),
  adminUserDetail: (userId) => api.get(`/verification/admin/user/${userId}`),
  adminReview: (userId, data) => api.post(`/verification/admin/review/${userId}`, data),
  adminExpiringDocs: (days) => api.get('/verification/admin/expiring-documents', { params: { days } }),
  adminAuditLog: (params) => api.get('/verification/admin/audit-log', { params }),
  viewDocument: (docId) => api.get(`/verification/document/${docId}/view`),
};

// Reviews API
export const reviewsApi = {
  create: (data) => api.post('/reviews', data),
  getBySupplier: (supplierId, params) => api.get(`/reviews/${supplierId}`, { params }),
};

// Dashboard API
export const dashboardApi = {
  getSupplier: () => api.get('/dashboard/supplier'),
  getBuyer: () => api.get('/dashboard/buyer'),
};

// Activity API
export const activityApi = {
  getLive: () => api.get('/activity/live'),
};

// Account Manager API
export const accountManagerApi = {
  getManager: () => api.get('/account-manager'),
};

// Agent API
export const agentApi = {
  getDashboard: () => api.get('/dashboard/agent'),
  updateProfile: (data) => api.put('/agent/profile', data),
  representProduct: (productId) => api.post('/agent/represent-product', { product_id: productId }),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  verifyUser: (userId) => api.put(`/admin/users/${userId}/verify`),
  getVerificationRequests: () => api.get('/admin/verification-requests'),
  verifySupplier: (userId, verified) => api.put(`/admin/verify-supplier/${userId}`, { verified }),
};

// Premium Channel API
export const channelApi = {
  getPosts: (params) => api.get('/channel/posts', { params }),
  getPost: (id) => api.get(`/channel/posts/${id}`),
  createPost: (data) => api.post('/channel/posts', data),
  updatePost: (id, data) => api.put(`/channel/posts/${id}`, data),
  deletePost: (id) => api.delete(`/channel/posts/${id}`),
  getStats: () => api.get('/channel/stats'),
  getCategories: () => api.get('/channel/categories'),
  checkAccess: () => api.get('/channel/access/check'),
  grantAccess: (data) => api.post('/channel/access/grant', data),
  revokeAccess: (data) => api.post('/channel/access/revoke', data),
  getMembers: (params) => api.get('/channel/access/members', { params }),
  getAuditLogs: (params) => api.get('/channel/audit-logs', { params }),
  inviteAdmin: (data) => api.post('/channel/admin/invite', data),
  acceptInvite: (code) => api.post(`/channel/admin/invite/accept/${code}`),
  getTeam: () => api.get('/channel/admin/team'),
  updatePermissions: (data) => api.put('/channel/admin/permissions', data),
  removeAdmin: (userId) => api.delete(`/channel/admin/remove/${userId}`),
  // Notifications
  getNotifications: (params) => api.get('/channel/notifications', { params }),
  getNotificationCount: () => api.get('/channel/notifications/count'),
  markRead: (notifId) => api.put(`/channel/notifications/${notifId}/read`),
  markAllRead: () => api.put('/channel/notifications/read-all'),
  getNotifPreferences: () => api.get('/channel/notifications/preferences'),
  updateNotifPreferences: (data) => api.put('/channel/notifications/preferences', data),
  getNotifSettings: () => api.get('/channel/notifications/settings'),
  updateNotifSettings: (data) => api.put('/channel/notifications/settings', data),
  getNotifLog: (params) => api.get('/channel/notifications/log', { params }),
  // Push
  getVapidKey: () => api.get('/channel/push/vapid-key'),
  pushSubscribe: (data) => api.post('/channel/push/subscribe', data),
  pushUnsubscribe: () => api.delete('/channel/push/unsubscribe'),
  testPush: () => api.post('/channel/push/test'),
};

export default api;
